import 'flatpickr/dist/themes/airbnb.css';
import 'flatpickr/dist/plugins/monthSelect/style.css';

import { Dayjs } from 'dayjs';
import { Japanese } from 'flatpickr/dist/l10n/ja';
import monthSelectPlugin from 'flatpickr/dist/plugins/monthSelect';
import { useMemo } from 'react';
import * as React from 'react';
import Flatpickr from 'react-flatpickr';
import { useLocation, useNavigate } from 'react-router-dom';

import { DATE_PARAM_KEY } from '@/config/constants/query';
import { dayjst } from '@/lib/dayjst';

import { Date, DownIcon, NextLink, PrevLink, Wrapper } from './style';

interface DateSelectorProps {
  date: Dayjs;
  displayFormat: string;
  pathFormat: string;
  unit?: 'day' | 'month' | 'year';
  clickOpens?: boolean;
}

const DateSelector: React.FC<DateSelectorProps> = React.memo(
  ({
    date,
    displayFormat = 'YYYY年M月D日',
    pathFormat = 'YYYY-MM-DD',
    unit = 'day',
    clickOpens = true,
  }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const prevDate = useMemo(() => date.subtract(1, unit), [date]);
    const nextDate = useMemo(() => date.add(1, unit), [date]);

    const getHref = (_date: Dayjs) => {
      const queryParams = new URLSearchParams(location.search);
      queryParams.set(DATE_PARAM_KEY, _date.format(pathFormat));
      return `${location.pathname}?${queryParams.toString()}`;
    };

    return (
      <Wrapper>
        <PrevLink to={getHref(prevDate)} />
        <Flatpickr
          value={date.toDate()}
          onChange={([d]) => navigate(getHref(dayjst(d as Date)))}
          options={{
            clickOpens,
            locale: Japanese,
            disableMobile: true,
            plugins: ['month', 'year'].includes(unit)
              ? [monthSelectPlugin()]
              : [],
          }}
          render={({ value }, ref) => {
            return (
              <Date ref={ref} disabled={!clickOpens}>
                <span>{dayjst(value as Date).format(displayFormat)}</span>
                {clickOpens && <DownIcon />}
              </Date>
            );
          }}
        />
        <NextLink to={getHref(nextDate)} />
      </Wrapper>
    );
  },
);

export default DateSelector;
